<template>
    <div class="send-form" ref="sendForm">
        <div v-if="!sendEmailView && !link">
            <div class="title"><span class="icon far fa-inbox-out"></span>Send Form</div>
            <div class="subtitle">Choose how to send this form</div>
            <template v-if="!selectedSendTo">
                <div v-for="(contact, index) in sendTos" :key="index">
                    <div class="send-to disabled" v-if="!contact.email" title="Missing email address">
                        Send {{ contact.sendingBack ? 'back' : '' }} to {{ contact.fname }} {{ contact.lname }}
                        <span class="far fa-exclamation-triangle"></span>
                    </div>
                    <div
                        class="send-to"
                        @click="
                            () => {
                                emailAddress = contact.id ? null : contact.email;
                                localSendingToContactId = contact.id || null;
                            }
                        "
                        v-else
                    >
                        <template v-if="contact.fname || contact.lname">
                            Send {{ contact.sendingBack ? 'back' : '' }} to {{ contact.fname }} {{ contact.lname }} ({{
                                contact.email
                            }})
                        </template>
                        <template v-else>
                            Send {{ contact.sendingBack ? 'back' : '' }} to {{ contact.email }}
                        </template>
                    </div>
                </div>
            </template>
            <div class="sending-to" v-else>
                <div class="to">
                    Sending To {{ selectedSendTo.fname }} {{ selectedSendTo.lname }} ({{ selectedSendTo.email }})
                </div>
                <div
                    class="change"
                    @click="
                        resetSettings();
                        localSendingToContactId = null;
                    "
                >
                    Change
                </div>
            </div>

            <div class="send" @click="chooseLink()" :class="{ disabled: isSaving }">
                <div class="icon">
                    <i class="far fa-link"></i>
                </div>
                <div class="v-split">
                    <div class="heading">Get Link to Form</div>
                    <div class="info">Get a link to copy and paste into your own email or message.</div>
                </div>
            </div>
            <div class="send" @click="chooseEmail()" :class="{ disabled: isSaving }">
                <div class="icon">
                    <i class="far fa-envelope-open-text"></i>
                </div>
                <div class="v-split">
                    <div class="heading">Send by Email</div>
                    <div class="info">
                        Send directly by email from {{ appName }}.
                    </div>
                </div>
            </div>
            <div class="send" @click="chooseInternal()" :class="{ disabled: isSaving }" v-if="!selectedSendTo">
                <div class="icon">
                    <i class="far fa-user-friends"></i>
                </div>
                <div class="v-split">
                    <div class="heading">Send Internally</div>
                    <div class="info">Send this form to another member of your organization.</div>
                </div>
            </div>
        </div>
        <div v-if="link">
            <div class="title">
                <i class="icon far fa-inbox-out"></i>
                {{ publicLink ? 'Reusable' : 'One-Time' }} Link
            </div>
            <copy-text class="link" :text="link"/>
            <send-options ref="linkOptions" :contactId="localSendingToContactId" />
            <div class="bottom-buttons">
                <button @click="resetSettings" v-if="!publicLink">Back</button>
                <span v-else></span>
                <button @click="close" class="primary">Done</button>
            </div>
        </div>
        <div v-if="sendEmailView">
            <div class="title">
                <i class="icon far fa-inbox-out"></i>
                Send Form {{ isInternal ? 'Internally' : 'by Email' }}
            </div>
            <user-chooser
                v-if="isInternal"
                v-model="localSendingToValue"
                :hideAuthUser="true"
                :fullWidth="true"
                :hideUsers="assignedToBoxId ? `box_${assignedToBoxId}` : assignedToUserId"
                :class="{
                    ['localSendingTo' + id]: true,
                    error: errorElems.includes('localSendingTo'),
                }"
            />
            <form-input
                v-else
                label="Email Address"
                :autoFocus="!emailAddress"
                v-model.trim="emailAddress"
                :inputClasses="{
                    ['emailAddress' + id]: true,
                    error: errorElems.includes('emailAddress'),
                }"
            />
            <form-text-area label="Message" v-model="emailBody" height="200px" resize="none" />
            <send-options
                v-if="!isInternal"
                :autoSave="false"
                ref="emailOptions"
                :contactId="localSendingToContactId"
            />
            <div class="bottom-buttons">
                <button @click="resetSettings">Back</button>
                <submit-button :click="sendEmail" class="primary">Send</submit-button>
            </div>
        </div>
    </div>
</template>

<script>
    import { strings } from '../../utils';
    import { mapGetters } from 'vuex';
    import isEmail from 'validator/es/lib/isEmail';
    import CopyText from '../CopyText';
    import FormTextArea from '../FormTextArea';
    import SendOptions from './SendOptions';
    import UserChooser from '../UserChooser';

    export default {
        name: 'SendForm',
        components: { CopyText, FormTextArea, SendOptions, UserChooser },
        data() {
            return {
                id: strings.random(),
                link: '',
                emailAddress: '',
                emailBody: '',
                errorElems: [],
                isInternal: false,
                sendEmailView: false,
                isSaving: false,
                localSendingToUserId: null,
                localSendingToBoxId: null,
                localSendingToContactId: null,
                reloadFiller: false,
            };
        },
        props: {
            publicLink: {
                type: String,
                default: '',
            },
            requiresReload: {
                type: Boolean,
                default: false,
            },
        },
        computed: {
            ...mapGetters({
                tooltip: 'tooltip/get',
                pkg: 'package/pkg',
                assignedToUserId: 'office/assignedToUserId',
                assignedToBoxId: 'office/assignedToBoxId',
                org: 'org/org',
                pkgLink: 'package/link',
                contactInfo: 'package/getContactInfo',
                history: 'package/history',
                sendingToUserId: 'office/sendingToUserId',
                sendingToBoxId: 'office/sendingToBoxId',
                sendingToContactId: 'office/sendingToContactId',
                users: 'org/users',
                parseBoxString: 'boxes/parseBoxString',
                availableBoxes: 'boxes/availableBoxes',
                appName: 'base/appName',
            }),
            selectedSendTo() {
                if (!this.emailAddress && !this.localSendingToContactId) return false;

                return (
                    this.sendTos.find((sendTo) => sendTo.email === this.emailAddress) ||
                    this.sendTos.find((sendTo) => sendTo.id === this.localSendingToContactId)
                );
            },
            localSendingToValue: {
                // value for 'sending to' user chooser
                get() {
                    if (this.localSendingToUserId) return this.localSendingToUserId;
                    else if (this.localSendingToBoxId) return 'box_' + this.localSendingToBoxId;
                    return '';
                },
                set(val) {
                    let details = this.parseBoxString(val);
                    if (details.type === 'box') {
                        this.localSendingToBoxId = details.id;
                        this.localSendingToUserId = '';
                    } else {
                        this.localSendingToUserId = details.id;
                        this.localSendingToBoxId = '';
                    }
                },
            },
            sendTos() {
                // returns array of previous fillers, and contacts who are selected on the form
                let previousFillers = this.history.reduce((acc, item) => {
                    if (item.action === 'revision' && item.submitted_by_email) {
                        let existing = acc.find((i) => i.email === item.submitted_by_email);
                        if (existing) {
                            if (!existing.lname && item.submitted_by_lname) {
                                existing.lname = item.submitted_by_lname;
                                existing.fname = item.submitted_by_fname;
                            }
                        } else {
                            acc.push({
                                fname: item.submitted_by_fname || '',
                                lname: item.submitted_by_lname || '',
                                email: item.submitted_by_email,
                                sendingBack: true,
                            });
                        }
                    }
                    return acc;
                }, []);

                // if a contact is already in the previous fillers list, update that list and don't add them
                let contactInfo = this.contactInfo.reduce((acc, contact) => {
                    let previousFiller = previousFillers.find((filler) => {
                        return filler.email.trim().toLowerCase() === contact.email.trim().toLowerCase();
                    });
                    if (previousFiller) {
                        previousFiller.id = contact.id;
                    } else {
                        acc.push({
                            fname: contact.fname,
                            lname: contact.lname,
                            email: contact.email,
                            id: contact.id,
                        });
                    }
                    return acc;
                }, []);

                return previousFillers.concat(contactInfo);
            },
        },
        methods: {
            async send() {
                // If 'sending to' user/contact has changed, send email as the default 'sendingTo' value.
                // The API will update to fname/lname if possible
                if (
                    this.sendingToContactId !== this.localSendingToContactId ||
                    this.sendingToUserId !== this.localSendingToUserId ||
                    this.sendingToBoxId !== this.localSendingToBoxId ||
                    (this.emailAddress && !this.isInternal)
                ) {
                    this.$store.commit('office/sendingTo', this.emailAddress || '');
                }

                if (this.localSendingToContactId || (this.emailAddress && !this.isInternal)) {
                    this.localSendingToUserId = null;
                    this.localSendingToBoxId = null;
                }

                this.$store.commit('office/sendingToUserId', this.localSendingToUserId);
                this.$store.commit('office/sendingToBoxId', this.localSendingToBoxId);
                this.$store.commit('office/sendingToContactId', this.localSendingToContactId);
                this.$store.commit('office/sendEmail', this.sendEmailView);
                this.$store.commit('office/email', {
                    address: this.isInternal ? '' : this.emailAddress,
                    body: this.emailBody,
                });

                let res = await this.$store.dispatch('package/save', 'send');
                if (res.message) {
                    await this.$store.dispatch('dialog/set', { type: 'info', body: res.message });
                }

                if (!this.reloadFiller) this.reloadFiller = res && res.action === 'save';
                this.$store.dispatch('office/changeMode', 'review');
            },
            resetSettings() {
                if (this.$refs.linkOptions && !this.$refs.linkOptions.validate()) return;
                this.link = this.publicLink || '';
                this.emailAddress = '';
                this.emailBody = '';
                this.isInternal = false;
                this.sendEmailView = false;
                this.$store.commit('dialog/dialogProps', { showX: true });
            },
            async chooseLink() {
                this.isSaving = true;
                await this.send();
                this.link = this.pkgLink;
                this.isSaving = false;
                if (this.org.settings.requirePin) {
                    this.$store.commit('dialog/dialogProps', { showX: false });
                }
            },
            chooseEmail() {
                if (this.isSaving) return;
                this.sendEmailView = true;
                if (this.selectedSendTo) {
                    this.emailAddress = this.selectedSendTo.email;
                }
            },
            chooseInternal() {
                if (this.isSaving) return;
                this.sendEmailView = true;
                this.isInternal = true;
            },
            validateEmail() {
                if (this.isInternal || isEmail(this.emailAddress)) return true;

                this.$store.commit('tooltip/set', {
                    message: 'Invalid email address',
                    trigger: `.emailAddress${this.id}`,
                    class: 'error',
                });
                this.errorElems.push('emailAddress');
                return false;
            },
            validateUser() {
                if (!this.isInternal || this.localSendingToUserId || this.localSendingToBoxId) return true;

                this.$store.commit('tooltip/set', {
                    message: 'Please select a user',
                    trigger: `.emailUser${this.id}`,
                    class: 'error',
                });
                this.errorElems.push('emailUser');
                return false;
            },
            async sendEmail() {
                if (!this.validateEmail()) return;
                if (!this.validateUser()) return;
                if (this.$refs.emailOptions && !this.$refs.emailOptions.validate()) return;

                await this.send();

                if (this.$refs.emailOptions) {
                    await this.$refs.emailOptions.save();
                }

                this.close();
            },
            close() {
                if (this.$refs.linkOptions && !this.$refs.linkOptions.validate()) return;
                this.reloadFiller = false;
                this.$store.commit('dialog/clear');

                if (this.pkg.is_public) {
                    this.$router.push(`/boxes/0/public`);
                    return;
                }

                // route to the assigned user/group's pending box, if current user has access
                let boxIndex = 0;
                if (this.assignedToUserId) {
                    boxIndex = this.availableBoxes.findIndex((box) => {
                        return box.info.type === 'user' && box.info.id === this.assignedToUserId;
                    });
                } else if (this.assignedToBoxId) {
                    boxIndex = this.availableBoxes.findIndex((box) => {
                        return box.info.type === 'org' && box.info.id === this.assignedToBoxId;
                    });
                }

                if (boxIndex === -1) boxIndex = 0; // default to current user's box
                this.$router.push(`/boxes/${boxIndex}/pending`);
            },
        },
        async mounted() {
            this.link = this.publicLink || '';

            if (this.pkg.status !== 'Active') {
                this.localSendingToUserId = this.$store.getters['office/sendingToUserId'];
                this.localSendingToBoxId = this.$store.getters['office/sendingToBoxId'];
                this.localSendingToContactId = this.$store.getters['office/sendingToContactId'];
            }

            this.reloadFiller = this.requiresReload;
        },
        watch: {
            tooltip(tt) {
                if (!tt) this.errorElems = [];
            },
        },
        async beforeDestroy() {
            if (this.reloadFiller) {
                this.$loading('Refreshing Package');
                await this.$store.dispatch('filler/load', { id: this.pkg.id });
                this.$loading(false);
            }
        },
    };
</script>

<style lang="scss" scoped>
    $height-title: 25px;
    $padding-container: 1em;

    .send-form {
        font-size: $font-size-smaller;
        padding: 0.5em;
        overflow: hidden;
        position: relative;
        height: 'auto';
    }

    .title {
        font-weight: $bold;
        font-size: $font-size-larger;
        margin-bottom: 0.35em;
        height: $height-title;

        .icon {
            margin-right: 0.35em;
        }
    }

    .subtitle {
        color: $color-disabled-text;
        margin-bottom: 0.5em;
    }

    .send-to {
        @extend a;
        padding: 0.1em 0.5em;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        padding-right: 1em;
        margin-bottom: 0.5em;

        &.disabled {
            cursor: auto;
            color: $color-inactive-text;

            span {
                padding-left: 0.15em;
                color: $color-error;
            }
        }
    }

    .sending-to {
        margin-bottom: 0.5em;
        display: flex;
        justify-content: space-between;

        .to {
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            padding-right: 1em;
        }

        .change {
            @extend a;
        }
    }

    .send {
        @include border-radius-small;
        @include border;

        display: flex;
        cursor: pointer;
        transition: color 0.2s, border-color 0.2s, background-color 0.2s;

        &:hover {
            color: $color-white;
            background: $color-highlight;
            border-color: $color-highlight;
        }

        &:not(:last-child) {
            margin-bottom: 0.5em;
        }

        &.disabled {
            background-color: $color-disabled-bg;
            color: $color-disabled-text;
        }

        .icon {
            display: flex;
            width: 50px;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;

            i {
                font-size: 20px;
            }
        }

        .v-split {
            flex-grow: 1;
            display: flex;
            flex-direction: column;
            padding: 0.5em 0.75em 0.5em 0;

            .heading {
                font-weight: $bold;
                margin-bottom: 0.25em;
            }
        }
    }

    .bottom-buttons {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-top: 1em;
    }

    .send-options {
        margin-top: 1em;
    }
</style>